import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, ThumbsUp, MessageSquare, Share2, Zap } from "lucide-react";

interface ViralPost {
  postId: string;
  author: string;
  authorPosition?: string;
  text: string;
  engagement: number;
  engagementMultiplier: number;
  publishedAt: string;
}

interface ViralContentShowcaseProps {
  viralPosts: ViralPost[];
  avgEngagement: number;
}

export const ViralContentShowcase = ({ viralPosts, avgEngagement }: ViralContentShowcaseProps) => {
  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('pl-PL', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  if (viralPosts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Viral Content
          </CardTitle>
          <CardDescription>
            No viral content identified (2x+ average engagement)
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Viral Content
        </CardTitle>
        <CardDescription>
          Posts with 2x+ average engagement ({Math.round(avgEngagement * 2)}+ interactions)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {viralPosts.slice(0, 5).map((post, index) => (
            <div
              key={post.postId || index}
              className="p-4 rounded-lg border border-primary/20 bg-gradient-to-br from-primary/5 to-background hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold">{post.author}</h4>
                    <Badge variant="secondary" className="text-xs">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      {post.engagementMultiplier}x viral
                    </Badge>
                  </div>
                  {post.authorPosition && (
                    <p className="text-xs text-muted-foreground">{post.authorPosition}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-primary">{post.engagement}</p>
                  <p className="text-xs text-muted-foreground">interactions</p>
                </div>
              </div>

              <p className="text-sm text-foreground/80 mb-3 line-clamp-3">
                {post.text}
              </p>

              <div className="flex items-center justify-between pt-3 border-t border-border/50">
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <ThumbsUp className="h-3 w-3" />
                    Likes
                  </span>
                  <span className="flex items-center gap-1">
                    <MessageSquare className="h-3 w-3" />
                    Comments
                  </span>
                  <span className="flex items-center gap-1">
                    <Share2 className="h-3 w-3" />
                    Shares
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  {formatDate(post.publishedAt)}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-semibold mb-2 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Viral Content Insights
          </h4>
          <p className="text-sm text-muted-foreground">
            These posts significantly outperformed average engagement. Analyze common 
            elements like posting time, content format, and topics to replicate success.
            Average engagement: <strong>{Math.round(avgEngagement)}</strong> interactions.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};
